import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { toEnglishUserMessage } from '../lib/messageI18n';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

type ToastItem = { id: number; message: string; type: ToastType };

type ToastContextValue = {
  showToast: (message: string, type?: ToastType, durationMs?: number) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

function ToastView({
  item,
  onClose,
}: {
  item: ToastItem;
  onClose: (id: number) => void;
}) {
  const cfg: Record<ToastType, { icon: string; cls: string; closeWhite: boolean }> = {
    success: { icon: 'fa-check-circle', cls: 'bg-success text-white', closeWhite: true },
    error: { icon: 'fa-times-circle', cls: 'bg-danger text-white', closeWhite: true },
    warning: { icon: 'fa-exclamation-triangle', cls: 'bg-warning text-dark', closeWhite: false },
    info: { icon: 'fa-info-circle', cls: 'bg-info text-white', closeWhite: true },
  };
  const c = cfg[item.type];
  return (
    <div
      className={`${c.cls} p-3 rounded-3 shadow-lg iptv-toast-enter`}
      style={{ minWidth: 280 }}
    >
      <div className="d-flex align-items-center">
        <div className="flex-shrink-0">
          <i className={`fas ${c.icon} me-2`} />
        </div>
        <div className="flex-grow-1 small iptv-toast-message">{item.message}</div>
        <button
          type="button"
          className={`btn-close ms-2 ${c.closeWhite ? 'btn-close-white' : ''}`}
          style={{ fontSize: '0.7rem' }}
          aria-label="Close"
          onClick={() => onClose(item.id)}
        />
      </div>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', durationMs = 3000) => {
      const safeMessage = toEnglishUserMessage(message);
      const id = Date.now() + Math.random();
      setItems((prev) => {
        const next = [...prev, { id, message: safeMessage, type }];
        if (next.length > 3) next.shift();
        return next;
      });
      window.setTimeout(() => remove(id), durationMs);
    },
    [remove],
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  const host =
    typeof document !== 'undefined'
      ? createPortal(
          <div className="iptv-toast-host">
            {items.map((t) => (
              <ToastView key={t.id} item={t} onClose={remove} />
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <ToastContext.Provider value={value}>
      {children}
      {host}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast requires ToastProvider');
  return ctx;
}
